import React, { useState } from 'react'
import styled from 'styled-components'
import { Icon } from './Icon'

export type AbwAccordionItem = {
	id: string
	/** Row header — plain text or a custom node (title + badges + meta). */
	header: React.ReactNode
	/** Collapsible body. */
	content: React.ReactNode
}

export type AbwAccordionProps = {
	items: AbwAccordionItem[]
	/** Only one item open at a time (FAQ behaviour). */
	single?: boolean
	/** Ids expanded on first render. */
	defaultOpen?: string[]
	className?: string
}

export type AbwAccordionRowProps = {
	header: React.ReactNode
	open: boolean
	onToggle: () => void
	className?: string
}

/** One controlled accordion row — white rounded card, rotating chevron, animated height. */
export const AccordionRow: React.FC<AbwAccordionRowProps> = ({ header, open, onToggle, className, children }) => (
	<Row className={className}>
		<HeaderButton type="button" onClick={onToggle} aria-expanded={open}>
			<span className="header-content">{header}</span>
			<Chevron data-open={open}>
				<Icon name="chevron-down" size={18} />
			</Chevron>
		</HeaderButton>
		<Body data-open={open} aria-hidden={!open}>
			<div className="clip">
				<div className="inner">{children}</div>
			</div>
		</Body>
	</Row>
)

/**
 * Rounded-card accordion — white rows, rotating chevron, animated height
 * (CSS grid-rows so content height stays unknown). Uncontrolled.
 */
export const Accordion: React.FC<AbwAccordionProps> = ({ items, single, defaultOpen = [], className }) => {
	const [open, setOpen] = useState<string[]>(defaultOpen)

	const toggle = (id: string) => {
		setOpen(prev => {
			const isOpen = prev.includes(id)
			if (single) return isOpen ? [] : [id]
			return isOpen ? prev.filter(x => x !== id) : [...prev, id]
		})
	}

	return (
		<List className={className}>
			{items.map(item => (
				<AccordionRow key={item.id} header={item.header} open={open.includes(item.id)} onToggle={() => toggle(item.id)}>
					{item.content}
				</AccordionRow>
			))}
		</List>
	)
}

const List = styled.div`
	display: flex;
	flex-direction: column;
	gap: var(--space-2);
`
const Row = styled.div`
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: var(--r-xl);
	overflow: hidden;
`
const HeaderButton = styled.button`
	width: 100%;
	border: none;
	background: none;
	padding: 14px 16px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--space-3);
	cursor: pointer;
	font-family: var(--font-sans);
	text-align: start;
	min-height: var(--touch-min);
	.header-content {
		flex: 1;
		font: var(--fw-bold) var(--fs-body)/1.6 var(--font-sans);
		color: var(--text-primary);
	}
`
const Chevron = styled.span`
	flex: none;
	display: flex;
	color: var(--text-secondary);
	transition: transform var(--dur-base) var(--ease);
	&[data-open='true'] {
		transform: rotate(180deg);
	}
`
const Body = styled.div`
	display: grid;
	grid-template-rows: 0fr;
	transition: grid-template-rows var(--dur-base) var(--ease);
	&[data-open='true'] {
		grid-template-rows: 1fr;
	}
	.clip {
		overflow: hidden;
	}
	.inner {
		padding: 12px 16px 14px;
		border-top: 1px solid var(--border);
		font: var(--fw-regular) 13px/1.8 var(--font-sans);
		color: var(--text-secondary);
	}
`
